// src/pages/settings/Features.jsx
// ALL feature toggles in one place — moved here from Company tab.
import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api, { isLocalInstall } from '../../config/api';
import { usePushSubscription } from '../../hooks/usePushSubscription';

const GREEN = '#2e9f86';

const inp = {
  width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 8,
  padding: '8px 11px', fontSize: 13, outline: 'none', boxSizing: 'border-box',
  background: '#fafafa', color: '#111',
};

const SectionTitle = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10, marginTop: 4 }}>{children}</div>
);

function Toggle({ value, onChange, label, description, example, disabled }) {
  return (
    <div
      onClick={() => !disabled && onChange(!value)}
      style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
        padding: '11px 13px', border: `1.5px solid ${value ? '#a7f3d0' : '#e5e7eb'}`,
        borderRadius: 10, cursor: disabled ? 'default' : 'pointer',
        background: value ? '#f0fdf8' : '#fafafa', transition: 'all .15s',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>{label}</div>
        {description && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2, lineHeight: 1.5 }}>{description}</div>}
        {example && <div style={{ fontSize: 10.5, color: '#9ca3af', marginTop: 3, fontStyle: 'italic' }}>{example}</div>}
      </div>
      <div style={{ width: 42, height: 24, borderRadius: 20, flexShrink: 0, background: value ? GREEN : '#d1d5db', position: 'relative', transition: 'background .2s', marginTop: 2 }}>
        <div style={{ position: 'absolute', top: 2, left: value ? 20 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</label>
      {children}
    </div>
  );
}

export default function Features() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { supported: pushSupported, permission: pushPermission, subscribe: pushSubscribe, unsubscribe: pushUnsubscribe } = usePushSubscription(false);

  // ── POS & Sales ──────────────────────────────────────────────────────────
  const [variants,    setVariants]    = useState(true);
  const [creditSales, setCreditSales] = useState(true);
  const [priceLevels, setPriceLevels] = useState(false);
  const [discounts,   setDiscounts]   = useState(true);
  const [addOns,      setAddOns]      = useState(false);

  // ── Inventory ─────────────────────────────────────────────────────────────
  const [trackStock,   setTrackStock]   = useState(true);
  const [trackSerials, setTrackSerials] = useState(false);
  const [production,   setProduction]   = useState(false);
  const [scaleEnabled, setScaleEnabled] = useState(false);
  const [scalePrefix,  setScalePrefix]  = useState('20');
  const [scaleMode,    setScaleMode]    = useState('weight');

  // ── Operations ────────────────────────────────────────────────────────────
  const [delivery,  setDelivery]  = useState(false);
  const [locations, setLocations] = useState(false);
  const [shifts,    setShifts]    = useState(true);

  // ── Finance ───────────────────────────────────────────────────────────────
  const [tax,            setTax]            = useState(false);
  const [defaultTaxRate, setDefaultTaxRate] = useState(11);
  const [accounting,     setAccounting]     = useState(false);
  const [secondCurrency, setSecondCurrency] = useState(false);
  const [blockNegative,  setBlockNegative]  = useState(false);
  const [lowDefault,     setLowDefault]     = useState(5);
  const [termsDays,      setTermsDays]      = useState(0);

  // ── Label Printer ─────────────────────────────────────────────────────────
  const [labelPrinter,   setLabelPrinter]   = useState(false);
  const [labelWidthMm,   setLabelWidthMm]   = useState(50);
  const [labelHeightMm,  setLabelHeightMm]  = useState(30);
  const [labelColumns,   setLabelColumns]   = useState(1);
  const [labelMode,      setLabelMode]      = useState('roll'); // 'roll' | 'sheet'

  const [saved,    setSaved]    = useState(false);

  const { data: settings, isLoading } = useQuery(
    ['settings'],
    () => api.get('/api/settings').then(r => r.data),
  );

  useEffect(() => {
    if (!settings) return;
    const f = settings.features || {};
    const sc = settings.scale || {};
    if (f.variants        != null) setVariants(!!f.variants);
    if (f.credit_sales    != null) setCreditSales(!!f.credit_sales);
    if (f.price_levels    != null) setPriceLevels(!!f.price_levels);
    if (f.discounts       != null) setDiscounts(!!f.discounts);
    if (f.add_ons         != null) setAddOns(!!f.add_ons);
    if (f.track_stock     != null) setTrackStock(!!f.track_stock);
    if (f.track_serials   != null) setTrackSerials(!!f.track_serials);
    if (f.production      != null) setProduction(!!f.production);
    if (f.delivery        != null) setDelivery(!!f.delivery);
    if (f.locations       != null) setLocations(!!f.locations);
    if (f.shifts          != null) setShifts(!!f.shifts);
    if (f.tax             != null) setTax(!!f.tax);
    if (f.default_tax_rate != null) setDefaultTaxRate(Number(f.default_tax_rate) || 11);
    if (f.accounting      != null) setAccounting(!!f.accounting);
    if (f.second_currency != null) setSecondCurrency(!!f.second_currency);
    if (f.label_printer   != null) setLabelPrinter(!!f.label_printer);
    if (sc.enabled        != null) setScaleEnabled(!!sc.enabled);
    if (sc.prefix)                 setScalePrefix(sc.prefix);
    if (sc.mode)                   setScaleMode(sc.mode);
    const inv = settings.inventory || {}, acc = settings.accounting || {};
    setBlockNegative(!!inv.block_negative_stock);
    if (inv.low_stock_default != null) setLowDefault(Number(inv.low_stock_default) || 5);
    if (acc.default_terms_days != null) setTermsDays(Number(acc.default_terms_days) || 0);
    const lp = settings.label_printer_config || {};
    if (lp.width_mm)  setLabelWidthMm(Number(lp.width_mm)  || 50);
    if (lp.height_mm) setLabelHeightMm(Number(lp.height_mm) || 30);
    if (lp.columns)   setLabelColumns(Number(lp.columns)   || 1);
    if (lp.mode)      setLabelMode(lp.mode);
  }, [settings]);

  const save = useMutation(
    () => api.put('/api/settings', {
      features: {
        variants,
        credit_sales:     creditSales,
        price_levels:     priceLevels,
        discounts,
        add_ons:          addOns,
        track_stock:      trackStock,
        track_serials:    trackSerials,
        production,
        delivery,
        locations,
        shifts,
        tax,
        default_tax_rate: tax ? (Number(defaultTaxRate) || 0) : 0,
        accounting,
        second_currency: secondCurrency,
        label_printer: labelPrinter,
      },
      label_printer_config: labelPrinter ? {
        mode:      labelMode,
        width_mm:  Number(labelWidthMm)  || 50,
        height_mm: Number(labelHeightMm) || 30,
        columns:   Number(labelColumns)  || 1,
      } : {},
      inventory: { block_negative_stock: blockNegative, low_stock_default: Number(lowDefault) || 5 },
      accounting: { ...(settings?.accounting || {}), default_terms_days: Number(termsDays) || 0 },
      scale: {
        enabled: scaleEnabled,
        prefix:  String(scalePrefix || '20').replace(/\D/g, '').slice(0, 2) || '20',
        mode:    scaleMode === 'price' ? 'price' : 'weight',
      },
    }),
    {
      onSuccess: () => {
        qc.invalidateQueries(['settings']);
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      },
      onError: (e) => alert(e?.response?.data?.message || e.message),
    },
  );

  return (
    <div style={{ maxWidth: 760, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>

      {/* POS & Sales */}
      <div style={{ background: '#fff', border: '1.5px solid #f0f0f0', borderRadius: 12, padding: 18, marginBottom: 16 }}>
        <SectionTitle>🛒 POS &amp; Sales</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Toggle value={variants} onChange={setVariants}
            label="🎨 Product variants"
            description="One product in several sizes, colors, or models — each with its own price, barcode, and stock."
            example="e.g. T-shirt → S/M/L · iPhone → 128/256GB" />
          <Toggle value={creditSales} onChange={setCreditSales}
            label="💳 Credit sales"
            description="Sell on account — the customer pays later and the debt is tracked on their ledger."
            example="e.g. regular customers who settle at end of month" />
          <Toggle value={priceLevels} onChange={setPriceLevels}
            label="📊 Price levels"
            description="Different price lists for different customer types — assigned to accounts."
            example="e.g. Retail / Wholesale / VIP prices" />
          <Toggle value={discounts} onChange={setDiscounts}
            label="🏷️ POS discounts"
            description="Shows a discount field in the POS cart — % or fixed $ amount off the total." />
          <Toggle value={addOns} onChange={setAddOns}
            label="➕ Product add-ons"
            description="Attach option groups to products (e.g. Toppings, Size). A picker pops up when ringing up the item."
            example="e.g. burger → Cheese +$0.50, Extra Sauce free" />
        </div>
        {addOns && (
          <div style={{ marginTop: 10, fontSize: 12, color: '#065f46', background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 8, padding: '8px 12px' }}>
            ✓ Manage add-on groups in the <b>Products → Add-ons</b> tab. Then attach groups to products in the product form.
          </div>
        )}
      </div>

      {/* Inventory */}
      <div style={{ background: '#fff', border: '1.5px solid #f0f0f0', borderRadius: 12, padding: 18, marginBottom: 16 }}>
        <SectionTitle>📦 Inventory</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Toggle value={trackStock} onChange={setTrackStock}
            label="📦 Track stock"
            description="Inventory counts — stock goes down on sales, up on purchases, with full movement history."
            example="Turn off only if you never count inventory" />
          {trackStock && (
            <Toggle value={blockNegative} onChange={setBlockNegative}
              label="⛔ Don't sell more than the stock"
              description="A sale (or loss / purchase return) that would take an item below zero is refused, with the list of what is missing."
              example="Leave off if you sometimes sell before entering the purchase" />
          )}
          {trackStock && (
            <Field label="Low-stock level for items without their own minimum">
              <input type="number" min="0" value={lowDefault} onChange={e => setLowDefault(e.target.value)} style={inp} placeholder="5" />
            </Field>
          )}
          <Toggle value={trackSerials} onChange={setTrackSerials}
            label="🔢 Serial / IMEI tracking"
            description="Record a serial number on each sold item — for warranty and traceability."
            example="e.g. phones, laptops" />
          <Toggle value={production} onChange={setProduction}
            label="🏭 Production items"
            description="Create production-type products with a recipe of raw material ingredients. Selling a production item automatically deducts ingredient quantities from stock."
            example="e.g. pizza deducts cheese, dough, sauce" />
          <Toggle value={scaleEnabled} onChange={setScaleEnabled}
            label="⚖️ Weighing scale barcodes"
            description="Sell items by weight using EAN-13 barcodes printed by a weighing scale."
            example="e.g. cheese, meat, vegetables sold per kg" />
          {scaleEnabled && (
            <div style={{ gridColumn: '1/-1', border: '1.5px solid #fcd34d', background: '#fffbeb', borderRadius: 10, padding: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 8 }}>⚖️ Scale barcode setup — must match your scale's configuration</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <Field label="Barcode prefix (2 digits)">
                  <input value={scalePrefix} onChange={e => setScalePrefix(e.target.value.replace(/\D/g, '').slice(0, 2))} style={inp} placeholder="20" />
                </Field>
                <Field label="Embedded value">
                  <select value={scaleMode} onChange={e => setScaleMode(e.target.value)} style={inp}>
                    <option value="weight">Weight (grams)</option>
                    <option value="price">Price (2 decimals)</option>
                  </select>
                </Field>
              </div>
              <div style={{ fontSize: 11, color: '#92400e', marginTop: 8, lineHeight: 1.6 }}>
                Label layout: <b style={{ fontFamily: 'monospace' }}>{scalePrefix || '20'} + 5-digit PLU + 5-digit {scaleMode === 'price' ? 'price' : 'weight'} + check digit</b>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Operations */}
      <div style={{ background: '#fff', border: '1.5px solid #f0f0f0', borderRadius: 12, padding: 18, marginBottom: 16 }}>
        <SectionTitle>🚀 Operations</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Toggle value={delivery} onChange={setDelivery}
            label="🚚 Delivery module"
            description="Take delivery orders from POS, assign drivers, track status, and settle cash."
            example="e.g. restaurant or mini-market with delivery bikes" />
          <Toggle value={shifts} onChange={setShifts}
            label="💵 Cashier shifts"
            description="Open/close shift with cash count, handover between cashiers, Z-report. Never blocks selling." />
          <Toggle value={locations} onChange={setLocations}
            label="🏬 Multiple branches"
            description="Tag documents and users per branch — for businesses with more than one location."
            example="e.g. main store + warehouse" />
        </div>
        {delivery && (
          <div style={{ marginTop: 10, fontSize: 12, color: '#065f46', background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 8, padding: '8px 12px' }}>
            ✓ Delivery Board is now visible in the sidebar. Use the 🚚 button in POS to create delivery orders.
          </div>
        )}
      </div>

      {/* Finance */}
      <div style={{ background: '#fff', border: '1.5px solid #f0f0f0', borderRadius: 12, padding: 18, marginBottom: 16 }}>
        <SectionTitle>💰 Finance &amp; Accounting</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Toggle value={tax} onChange={setTax}
            label="🧾 VAT / Tax"
            description="Enable VAT on documents and POS. Tax is inclusive — extracted from the selling price."
            example="e.g. 11% TVA in Lebanon" />
          {tax && (
            <Field label="Default Tax Rate (%)">
              <input type="number" min="0" step="any" value={defaultTaxRate}
                onChange={e => setDefaultTaxRate(e.target.value)} style={inp} placeholder="11" />
            </Field>
          )}
          <Toggle value={accounting} onChange={setAccounting}
            label="📈 Official accounting (journal, TVA)"
            description="Double-entry journal, trial balance, VAT report and manual entries — for your accountant." />
          <Toggle value={secondCurrency} onChange={setSecondCurrency}
            label="💱 Second currency (USD + LBP)"
            description="Take and record payments in either currency, and split the drawer, cashflow and reports per currency. Turn off for a single-currency shop."
            example="Set the exchange rate in Company settings" />
          <Field label="Default payment terms for credit invoices (days)">
            <input type="number" min="0" value={termsDays} onChange={e => setTermsDays(e.target.value)} style={inp} placeholder="0 = pay on the spot" />
          </Field>
          {accounting && (
            <div style={{ gridColumn: '1/-1', fontSize: 12, color: '#065f46', background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 8, padding: '8px 12px' }}>
              ✓ After saving, <b>Accounting</b> appears in the sidebar (owner only).
              For the VAT report, also enable the Tax feature above. Each customer can have its own terms and credit limit (Accounts → edit).
            </div>
          )}
        </div>
      </div>

      {/* Label Printer */}
      <div style={{ background: '#fff', border: '1.5px solid #f0f0f0', borderRadius: 12, padding: 18, marginBottom: 16 }}>
        <SectionTitle>🏷️ Barcode Label Printer</SectionTitle>
        <Toggle value={labelPrinter} onChange={setLabelPrinter}
          label="🏷️ Barcode label printing"
          description="Enable the Labels button in the Products page to print barcodes on label rolls or A4 sheets." />
        {labelPrinter && (
          <div style={{ marginTop: 12, border: '1.5px solid #e5e7eb', borderRadius: 10, padding: 14, background: '#fafafa' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 10 }}>Label size &amp; layout</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <Field label="Print mode">
                <select value={labelMode} onChange={e => setLabelMode(e.target.value)} style={inp}>
                  <option value="roll">🗞️ Label roll (one label per page)</option>
                  <option value="sheet">📄 Sheet (A4, multiple per page)</option>
                </select>
              </Field>
              {labelMode === 'sheet' && (
                <Field label="Labels per row">
                  <select value={labelColumns} onChange={e => setLabelColumns(Number(e.target.value))} style={inp}>
                    {[2,3,4,5].map(n => <option key={n} value={n}>{n} per row</option>)}
                  </select>
                </Field>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Field label="Label width (mm)">
                <input type="number" min="20" max="200" value={labelWidthMm} onChange={e => setLabelWidthMm(e.target.value)} style={inp} />
              </Field>
              <Field label="Label height (mm)">
                <input type="number" min="15" max="200" value={labelHeightMm} onChange={e => setLabelHeightMm(e.target.value)} style={inp} />
              </Field>
            </div>
            {/* common sizes hint */}
            <div style={{ marginTop: 10, fontSize: 11, color: '#9ca3af', lineHeight: 1.6 }}>
              Common roll sizes: &nbsp;
              {[['40×25',40,25],['50×30',50,30],['60×40',60,40],['100×50',100,50]].map(([label,w,h]) => (
                <button key={label} onClick={() => { setLabelWidthMm(w); setLabelHeightMm(h); setLabelMode('roll'); }}
                  style={{ background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 5, padding: '2px 8px', fontSize: 11, cursor: 'pointer', marginRight: 5, color: '#374151' }}>
                  {label}mm
                </button>
              ))}
            </div>
            {/* preview */}
            <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 11, color: '#6b7280' }}>Preview:</div>
              <div style={{
                border: '2px dashed #2e9f86', borderRadius: 4, background: '#f0fdf8',
                width: Math.min(Number(labelWidthMm) * 2, 160),
                height: Math.min(Number(labelHeightMm) * 2, 100),
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                fontSize: 9, color: '#374151', gap: 2, flexShrink: 0,
              }}>
                <div style={{ fontWeight: 700, fontSize: 10 }}>Product Name</div>
                <div style={{ background: '#374151', height: 16, width: '70%', borderRadius: 1 }} />
                <div style={{ fontSize: 9, color: '#6b7280' }}>1234567890</div>
                <div style={{ fontWeight: 700, color: '#2e9f86', fontSize: 10 }}>$10.00</div>
              </div>
              <div style={{ fontSize: 11, color: '#9ca3af' }}>{labelWidthMm}×{labelHeightMm} mm</div>
            </div>
          </div>
        )}
      </div>

      {/* Push Notifications — cloud only */}
      {pushSupported && !isLocalInstall && (
        <div style={{ background: '#fff', border: '1.5px solid #f0f0f0', borderRadius: 12, padding: 18, marginBottom: 16 }}>
          <SectionTitle>🔔 Push Notifications</SectionTitle>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
            Get notified instantly — new delivery orders, confirmed sales. Works on mobile too (add to home screen on iPhone).
          </div>
          {pushPermission === 'granted' ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, fontSize: 13, color: '#065f46', background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 8, padding: '8px 12px' }}>
                🔔 Notifications enabled on this device
              </div>
              <button onClick={pushUnsubscribe}
                style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 7, padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                Disable
              </button>
            </div>
          ) : pushPermission === 'denied' ? (
            <div style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '8px 12px' }}>
              ⚠️ Notifications blocked in browser. Go to browser Settings → Site Permissions → Notifications → allow this site.
            </div>
          ) : (
            <button onClick={pushSubscribe}
              style={{ background: GREEN, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              🔔 Enable Notifications on This Device
            </button>
          )}
        </div>
      )}

      {/* Tip */}
      <div style={{ fontSize: 12, color: '#065f46', background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 10, padding: '10px 14px', marginBottom: 16, lineHeight: 1.6 }}>
        💡 <b>Tip — services:</b> to sell repairs, labor, or fees, no feature needed. Create a product and turn off "Track Stock" in the product form — sells normally but never affects inventory.
      </div>

      {/* Save */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => save.mutate()} disabled={save.isLoading || isLoading}
          style={{ background: GREEN, color: '#fff', border: 'none', borderRadius: 9, padding: '11px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: (save.isLoading || isLoading) ? 0.5 : 1 }}>
          {save.isLoading ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
        </button>
        {saved && <span style={{ color: GREEN, fontWeight: 600, fontSize: 13 }}>✓ {t('common.saved', 'Saved')}</span>}
      </div>
    </div>
  );
}
